import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  switchMap,
  map,
  catchError,
  withLatestFrom,
  filter,
  mergeMap,
  take,
  concatMap,
  debounceTime,
} from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import * as fromActions from './image-browser.actions';
import * as fromSelectors from './image-browser.selectors';
import { ReportsService } from 'src/app/modules/report-components/services/report.service';
import { Store } from '@ngrx/store';
import { ImageBrowserState } from './image-browser.reducer';
import { ImagePreloaderService } from 'src/app/modules/shared/services/image-preloader.service';

@Injectable()
export class ImageBrowserEffects {
  //matches the window the paging effects request, so a jump reaches exactly one page further
  private static readonly JumpReachSeconds = 3600 * 4;

  constructor(
    private actions$: Actions,
    private service: ReportsService,
    private store: Store<{ imageBrowser: ImageBrowserState }>,
    private preloader: ImagePreloaderService,
  ) {}

  loadInitial$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.init),
      withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
      switchMap(([{ cameraId, start }, state]) =>
        this.service.getImages(cameraId, state.periodId, start.format('YYYY-MM-DDTHH:mm:ss'), 100, 100).pipe(
          map(images => fromActions.loadInitialImagesSuccess({ images })),
          catchError((error: Error) => of(fromActions.loadExtraImagesError({ error }))),
        ),
      ),
    ),
  );

  loadImagesBefore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadImagesBefore),
      withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
      filter(([{ fromIndex }, state]) => !!state.timeline[fromIndex]),
      switchMap(([{ before, fromIndex }, state]) =>
        this.service
          .getImages(
            state.cameraId,
            state.periodId,
            state.timeline[fromIndex].date.format('YYYY-MM-DDTHH:mm:ss'),
            before,
            0,
          )
          .pipe(
            map(images => fromActions.loadImagesBeforeSuccess({ images, fromIndex, before })),
            catchError((error: Error) => of(fromActions.loadExtraImagesError({ error }))),
          ),
      ),
    ),
  );

  loadImagesAfter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadImagesAfter),
      withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
      filter(([{ fromIndex }, state]) => !!state.timeline[fromIndex]),
      switchMap(([{ after, fromIndex }, state]) =>
        this.service
          .getImages(
            state.cameraId,
            state.periodId,
            state.timeline[fromIndex].date.format('YYYY-MM-DDTHH:mm:ss'),
            0,
            after,
          )
          .pipe(
            map(images => fromActions.loadImagesAfterSuccess({ images, fromIndex, after })),
            catchError((error: Error) => of(fromActions.loadExtraImagesError({ error }))),
          ),
      ),
    ),
  );

  cameraChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.selectedCameraChanged),
      withLatestFrom(
        this.store.select(fromSelectors.imageBrowserSelector),
        this.store.select(fromSelectors.selectCamerasToLoad),
      ),
      concatMap(([, state, camerasToLoad]) => {
        return camerasToLoad.map(cameraId =>
          fromActions.loadExtraCamera({
            cameraId,
            start: state.timeline[state.currentFrame].date,
          }),
        );
      }),
    ),
  );

  loadExtraCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadExtraCamera),
      withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
      switchMap(([action, state]) =>
        this.service
          .getImages(action.cameraId, state.periodId, action.start.format('YYYY-MM-DDTHH:mm:ss'), 20, 20)
          .pipe(
            map(images =>
              fromActions.loadExtraCameraSuccess({
                cameraId: action.cameraId,
                start: action.start,
                images,
              }),
            ),
            catchError((error: Error) => of(fromActions.loadExtraImagesError({ error }))),
          ),
      ),
    ),
  );

  preload(url: string) {
    return this.preloader.preloadImage(url).pipe(
      filter(loaded => loaded),
      take(1),
    );
  }

  preloadPipeline(store: ImageBrowserState, shortTermIndices: number[], longTermIndices: number[]) {
    if (!store.timeline[store.currentFrame]) {
      return of(true);
    }
    const shortTerm = shortTermIndices
      .map(index => store.timeline[index])
      .filter(img => !!img)
      .map(img => img.url);
    const shortTermExtra = shortTermIndices.reduce((prev, curr) => {
      let images = store.extraCamerasIds
        .map(cameraId => store.extraCamerasTimeline[cameraId].find(i => i.index === curr))
        .filter(img => !!img);
      return [...prev, ...images.map(x => x.url)];
    }, [] as string[]);
    const longTerm = longTermIndices
      .map(index => store.timeline[index])
      .filter(img => !!img)
      .map(img => img.url);
    const longTermExtra = longTermIndices.reduce((prev, curr) => {
      let images = store.extraCamerasIds
        .map(cameraId => store.extraCamerasTimeline[cameraId].find(i => i.index === curr))
        .filter(img => !!img);
      return [...prev, ...images.map(x => x.url)];
    }, [] as string[]);
    return of(null).pipe(
      mergeMap(() => this.preload(store.timeline[store.currentFrame].url)),
      mergeMap(() => forkJoin(...shortTerm.map(url => this.preload(url)))),
      mergeMap(() => forkJoin(...shortTermExtra.map(url => this.preload(url)))),
      mergeMap(() => forkJoin(...longTerm.map(url => this.preload(url)))),
      mergeMap(() => forkJoin(...longTermExtra.map(url => this.preload(url)))),
    );
  }

  preloadForPlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          fromActions.initFromTransaction,
          fromActions.loadInitialImagesSuccess,
          fromActions.goNext,
          fromActions.goPrev,
          fromActions.goNextN,
          fromActions.goTo,
          fromActions.goPrevN,
        ),
        withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
        filter(([action, state]) => state.playing),
        mergeMap(([action, state]) =>
          this.preloadPipeline(
            state,
            [state.currentFrame + 1, state.currentFrame + 2],
            [state.currentFrame + 3, state.currentFrame + 4, state.currentFrame + 5],
          ),
        ),
      ),
    { dispatch: false },
  );

  preloadForExtra$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.loadExtraCameraSuccess),
        withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
        mergeMap(([action, state]) =>
          this.preloadPipeline(
            state,
            [state.currentFrame + 1, state.currentFrame + 2],
            [state.currentFrame - 1, state.currentFrame - 2],
          ),
        ),
      ),
    { dispatch: false },
  );

  preloadForwardStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.goNext),
        withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
        filter(([_, state]) => !state.playing),
        mergeMap(([_, state]) =>
          this.preloadPipeline(
            state,
            [state.currentFrame + 1, state.currentFrame + 2],
            [state.currentFrame + 3, state.currentFrame + 4, state.currentFrame + 5],
          ),
        ),
      ),
    { dispatch: false },
  );
  preloadForwardNStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.goNextN),
        withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
        filter(([action, state]) => !state.playing),
        mergeMap(([action, state]) =>
          this.preloadPipeline(
            state,
            [state.currentFrame + action.count, state.currentFrame + action.count * 2],
            [state.currentFrame + action.count * 3, state.currentFrame + action.count * 4],
          ),
        ),
      ),
    { dispatch: false },
  );
  preloadBackwardStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.goPrev),
        withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
        filter(([_, state]) => !state.playing),
        mergeMap(([_, state]) =>
          this.preloadPipeline(
            state,
            [state.currentFrame - 1, state.currentFrame - 2],
            [state.currentFrame - 3, state.currentFrame - 4, state.currentFrame - 5],
          ),
        ),
      ),
    { dispatch: false },
  );
  preloadBackwardNStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.goPrevN),
        withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
        filter(([action, state]) => !state.playing),
        mergeMap(([action, state]) =>
          this.preloadPipeline(
            state,
            [state.currentFrame - action.count, state.currentFrame - action.count * 2],
            [state.currentFrame - action.count * 3, state.currentFrame - action.count * 4],
          ),
        ),
      ),
    { dispatch: false },
  );

  checkPreloadStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        fromActions.initFromTransaction,
        fromActions.loadInitialImagesSuccess,
        fromActions.goNext,
        fromActions.goPrev,
        fromActions.goNextN,
        fromActions.goTo,
        fromActions.goPrevN,
      ),
      withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
      switchMap(([action, state]) =>
        this.preloader
          .preloadImage(state.timeline[state.currentFrame] ? state.timeline[state.currentFrame].url : null)
          .pipe(
            filter(loaded => loaded),
            map(() => fromActions.imageLoaded()),
          ),
      ),
    ),
  );

  checkLoadMoreAfter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        fromActions.initFromTransaction,
        fromActions.loadInitialImagesSuccess,
        fromActions.goNext,
        fromActions.goPrev,
        fromActions.goNextN,
        fromActions.goTo,
        fromActions.goPrevN,
      ),
      withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
      filter(
        ([action, state]) =>
          !state.loadingAfter && state.currentFrame > state.timeline.length - 90 && state.hasMoreAfter,
      ),
      map(([action, state]) =>
        fromActions.loadImagesAfter({
          after: 3600 * 4,
          fromIndex: state.timeline.length - 1,
        }),
      ),
    ),
  );

  checkLoadMoreBefore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        fromActions.initFromTransaction,
        fromActions.loadInitialImagesSuccess,
        fromActions.goNext,
        fromActions.goPrev,
        fromActions.goNextN,
        fromActions.goTo,
        fromActions.goPrevN,
      ),
      withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
      filter(([, state]) => !state.loadingBefore && state.currentFrame < 15 && state.hasMoreBefore),
      map(([, state]) =>
        fromActions.loadImagesBefore({
          before: 3600 * 4,
          fromIndex: 0,
        }),
      ),
    ),
  );

  checkLoadMoreExtra$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.goNext, fromActions.goPrev, fromActions.goNextN, fromActions.goTo, fromActions.goPrevN),
      withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
      filter(([, state]) => {
        let someNotLoaded = state.extraCamerasIds.some(
          cameraId => state.extraCamerasTimeline[cameraId].findIndex(i => i.index === state.currentFrame) === -1,
        );
        return (
          state.extraCamerasIds.length > 0 &&
          state.extraCamerasIds.every(cameraId => !state.extraCamerasLoading[cameraId]) &&
          someNotLoaded &&
          state.hasMoreAfter
        );
      }),
      concatMap(([, state]) =>
        state.extraCamerasIds
          .filter(cameraId => !state.extraCamerasLoading[cameraId])
          .map(cameraId =>
            fromActions.loadExtraCamera({
              cameraId,
              start: state.timeline[state.currentFrame].date,
            }),
          ),
      ),
    ),
  );

  /**
   * A jump can aim past the frames paged in so far. Page toward it once and try again, rather than
   * asking over and over for a frame that is not there: this used to re-dispatch the same jump every
   * second for as long as the dialog stayed open.
   *
   * One page is as far as a jump may reach. The counterpart of a visit that straddles the report
   * boundary sits minutes beyond the edge, while one belonging to another report sits days beyond it
   * and is not ours to page to.
   */
  pageTowardsJump$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.goToTimestamp),
      withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
      filter(([, state]) => state.pendingJump != null && !state.loadingBefore && !state.loadingAfter),
      map(([, state]) => {
        const target = state.pendingJump;
        const page = ImageBrowserEffects.JumpReachSeconds;
        const first = state.timeline[0]?.date.valueOf();
        const last = state.timeline[state.timeline.length - 1]?.date.valueOf();

        if (last != null && target > last) {
          return state.hasMoreAfter && target - last <= page * 1000
            ? fromActions.loadImagesAfter({ after: page, fromIndex: state.timeline.length - 1 })
            : fromActions.jumpUnreachable();
        }

        if (first != null && target < first) {
          return state.hasMoreBefore && first - target <= page * 1000
            ? fromActions.loadImagesBefore({ before: page, fromIndex: 0 })
            : fromActions.jumpUnreachable();
        }

        return fromActions.jumpUnreachable();
      }),
    ),
  );

  resumeJumpWhenFramesArrive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadImagesAfterSuccess, fromActions.loadImagesBeforeSuccess),
      withLatestFrom(this.store.select(fromSelectors.imageBrowserSelector)),
      filter(([, state]) => state.pendingJump != null),
      map(([, state]) => fromActions.goToTimestamp({ timestamp: state.pendingJump })),
    ),
  );
}
